import type { FunctionComponent } from 'react';
import { Outlet, createRootRoute, createRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Button, EmptyState, cn } from '@eombor/ui';
import { Construction } from 'lucide-react';
import { t } from '../../shared/i18n/index.js';
import { LoginRoute } from '../../features/auth/ui/LoginRoute.js';
import { ChangePasswordGate } from '../../features/auth/ui/ChangePasswordGate.js';
import { RequireAuth } from '../../features/auth/ui/RequireAuth.js';
import { IncomingDocsPage } from '../../features/accounting/ui/IncomingDocsPage.js';
import { SurveysPage } from '../../features/accounting/ui/SurveysPage.js';
import { StatisticsPage } from '../../features/statistics/ui/StatisticsPage.js';
import { SupplyDetailRoute, SupplyListRoute, parseSupplyDetailSearch } from './supply.js';
import { CatalogPage } from '../../features/catalog/ui/CatalogPage.js';
import { OrgTreePage } from '../../features/org/ui/OrgTreePage.js';
import { MyWarehouseRoute } from './requisitions.js';
import { SubdivisionsPage } from '../../features/subdivision/ui/SubdivisionsPage.js';
import { parseWriteOffsSearch } from '../../features/writeoff/model/write-off-tabs.js';
import { parseRequisitionsSearch } from '../../features/requisition/model/requisitions-search.js';
import { parseMyWarehouseSearch } from '../../features/subdivision/model/my-warehouse-search.js';
import { RolesPage } from '../../features/rbac/ui/RolesPage.js';
import { AppShell } from '../layout/AppShell.js';
import { NAV_ITEMS } from '../layout/nav-model.js';
import { AssignmentsPage } from './assignments.js';
import { DashboardRoute } from './dashboard.js';
import { WriteOffsRoute } from './writeoffs.js';
import {
  HandoverRoute,
  InboxRoute,
  RequisitionCardRoute,
  RequisitionCreateRoute,
  RequisitionsListRoute,
} from './requisitions.js';
import { ReceiptRoute, StockSearchRoute } from './stock.js';
import {
  CellCardRoute,
  GLOBAL_STOCK_PATH,
  RECEIPT_PATH,
  WarehouseBuilderRoute,
  WarehouseDetailRoute,
  WarehouseStockRoute,
  WarehousesRoute,
} from './warehouses.js';

/**
 * Hali yozilmagan bo'lim. Bo'sh oq sahifa o'rniga nima bo'layotganini aytadi —
 * yon paneldagi havola hech qachon 404 ga olib bormaydi.
 */
const SectionPlaceholder = ({ title }: { title: string }) => {
  const navigate = useNavigate();
  return (
    <EmptyState
      className={cn('rounded-[var(--radius-lg)] border border-border bg-surface')}
      icon={<Construction size={20} aria-hidden />}
      title={title}
      description={t('section.soonDescription')}
      action={
        <Button variant="secondary" onClick={() => void navigate({ to: '/' })}>
          {t('section.backToDashboard')}
        </Button>
      }
    />
  );
};

export const NotFound = () => {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg p-6 text-text">
      <EmptyState
        title={t('notFound.title')}
        description={t('notFound.description')}
        action={
          <Button onClick={() => void navigate({ to: '/', replace: true })}>
            {t('notFound.action')}
          </Button>
        }
      />
    </div>
  );
};

const rootRoute = createRootRoute({ component: () => <Outlet /> });

/** Kirish sahifasi karkasdan **tashqarida** — u yerda navigatsiya yo'q. */
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginRoute,
});

/**
 * Kirish talab qilinadigan barcha sahifalar shu qatlam ostida. `RequireAuth`
 * qulf emas — u faqat tokensiz odamni bo'sh ekranda qoldirmaydi; ma'lumotni
 * backend himoya qiladi.
 *
 * `ChangePasswordGate` — undan KEYIN va qobiqdan OLDIN: vaqtinchalik parol
 * bilan kirgan xodim boshqa hech qaysi sahifani ochmasligi kerak, lekin
 * to'siqni ko'rishi uchun kirgan bo'lishi shart.
 */
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  component: () => (
    <RequireAuth>
      <ChangePasswordGate>
        <AppShell />
      </ChangePasswordGate>
    </RequireAuth>
  ),
});

const dashboardRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/',
  component: DashboardRoute,
});

/**
 * Yozilgan sahifalar. Ro'yxatda yo'q bo'lim `SectionPlaceholder` bilan
 * ochiladi — havola hech qachon 404 ga olib bormaydi.
 */
const SECTION_PAGES: Record<string, FunctionComponent> = {
  '/warehouses': StockSearchRoute,
  '/inbox': InboxRoute,
  '/handover': HandoverRoute,
  '/requisitions': RequisitionsListRoute,
  '/catalog': CatalogPage,
  '/supply': SupplyListRoute,
  '/accounting': IncomingDocsPage,
  '/accounting/surveys': SurveysPage,
  '/statistics': StatisticsPage,
  '/subdivisions': SubdivisionsPage,
  '/admin/org': OrgTreePage,
  '/admin/users': AssignmentsPage,
};

/**
 * Qolgan bo'limlar `NAV_ITEMS` dan quriladi — yon panelda ko'ringan har bir
 * havolaning marshruti bor, ro'yxat ikki joyda ayri ketmaydi.
 */
const SEARCH_SECTIONS: readonly string[] = ['/my-warehouse', '/write-offs'];

const sectionRoutes = NAV_ITEMS.filter(
  (item) => item.to !== '/' && !SEARCH_SECTIONS.includes(item.to),
).map((item) => {
  const Page = SECTION_PAGES[item.to];
  return createRoute({
    getParentRoute: () => shellRoute,
    path: item.to,
    /* Talabnomalar sahifasi ko'rinish va bildirgi bo'limini MANZILDA
       saqlaydi — yangilanganda odam tanlagan joyida qoladi. */
    ...(item.to === '/requisitions' ? { validateSearch: parseRequisitionsSearch } : {}),
    component: Page ?? (() => <SectionPlaceholder title={t(item.label)} />),
  });
});

/**
 * «Mening omborim» — ochiq bo'lim MANZILDA turadi (`?section=team`),
 * shuning uchun yangilash uni yo'qotmaydi va bo'limga havola berish
 * mumkin.
 *
 * Marshrut umumiy jadvaldan AJRATIB yozilgan: `validateSearch` ni
 * `NAV_ITEMS` ustidagi `map` ichida shartli qo'yib bo'lmaydi — router
 * o'shanda qidiruv turini yo'qotadi va `navigate({ search })` butun
 * ilova bo'ylab turdan chiqadi. Shu sabab u `SEARCH_SECTIONS` orqali
 * jadvaldan chiqarib tashlangan: yo'l baribir bitta marta e'lon
 * qilinadi.
 */
const myWarehouseRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/my-warehouse',
  validateSearch: parseMyWarehouseSearch,
  component: MyWarehouseRoute,
});

/** Rasxod aktlari navbati — faol tab manzilda (`?status=rejected`). */
const writeOffsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/write-offs',
  validateSearch: parseWriteOffsSearch,
  component: WriteOffsRoute,
});

/**
 * "Ombor" bo'limining ichki ekranlari — `NAV_ITEMS` da yo'q, chunki menyuda
 * bitta kirish nuqtasi turadi: unga ro'yxatdagi kartochkadan yoki
 * to'g'ridan-to'g'ri havola bilan kiriladi.
 *
 * Statik yo'llar (`/warehouses/stock`, `/warehouses/receipts/new`)
 * `$warehouseId` dan OLDIN e'lon qilinadi: aks holda "stock" so'zi
 * parametrga tushib, mavjud bo'lmagan omborning kartochkasi ochilardi.
 */
const warehouseListRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/warehouses/list',
  component: WarehousesRoute,
});

const receiptRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/warehouses/receipts/new',
  component: ReceiptRoute,
});

/**
 * Tushum hujjati. `$reportId` — identifikator, hujjat raqami (`T-2026-0001`)
 * emas: raqam yil boshida qaytadan sanaladi va havola bir kun boshqa
 * hujjatga olib borardi.
 */
const supplyDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/supply/$reportId',
  /*
   * `?place=` — joylashtirish sahifasi (vergul bilan satr id lari). U
   * manzilda turadi, shunda brauzerning "orqaga" tugmasi ham hujjatga
   * qaytaradi — ilgari bu faqat komponent holati edi va "orqaga" odamni
   * hujjatdan ham chiqarib, ro'yxatga otib yuborardi.
   */
  validateSearch: parseSupplyDetailSearch,
  component: SupplyDetailRoute,
});

const warehouseDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/warehouses/$warehouseId',
  /*
   * `?cell=` — skaner kartochkasidagi "Chizmada ko'rsatish" shu bilan keladi.
   * Tekshiruv MAJBURIY: manzilni odam ham yozishi mumkin va `cell` u yerda
   * massiv yoki son bo'lib chiqsa, sahifa `undefined` bilan yiqilardi.
   */
  validateSearch: (search: Record<string, unknown>): { cell?: string } =>
    typeof search['cell'] === 'string' ? { cell: search['cell'] } : {},
  component: WarehouseDetailRoute,
});

/**
 * Katak kartochkasi — skanerdan keyingi ekran. `$code` etiketkadagi kod
 * (`A-01-02-03`), `id` emas: havola odam o'qiy oladigan bo'lib qoladi.
 */
const cellCardRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/warehouses/$warehouseId/cells/$code',
  component: CellCardRoute,
});

/** Ombor kartochkasining "Mahsulotlar" ko'rinishi — alohida manzil bilan. */
const warehouseStockRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/warehouses/$warehouseId/stock',
  component: WarehouseStockRoute,
});

const warehouseBuilderRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/warehouses/$warehouseId/builder',
  component: WarehouseBuilderRoute,
});

/**
 * Eski manzillar. "Qoldiq" alohida bo'lim bo'lgan paytda berilgan havolalar
 * xatcho'plarda va oldingi ekranlarda qolgan — ular "sahifa topilmadi" ga
 * emas, yangi manzilga olib boradi. `replace` bilan: orqaga qaytish tugmasi
 * odamni yana shu yo'naltirishga tushirmasin.
 */
/**
 * Rollar — "Kirish huquqlari" bo'limining ikkinchi ko'rinishi. `NAV_ITEMS` da
 * yo'q: menyuda bitta kirish nuqtasi turadi, ko'rinish esa sahifa ichidagi
 * almashgich bilan tanlanadi.
 */
const rolesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/admin/roles',
  component: RolesPage,
});

const legacyGlobalStockRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/warehouses/stock',
  beforeLoad: () => {
    throw redirect({ to: GLOBAL_STOCK_PATH, replace: true });
  },
});

const legacyStockRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/stock',
  beforeLoad: () => {
    throw redirect({ to: GLOBAL_STOCK_PATH, replace: true });
  },
});

const legacyReceiptRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/stock/receipts/new',
  beforeLoad: () => {
    throw redirect({ to: RECEIPT_PATH, replace: true });
  },
});

/**
 * Talabnoma yaratish va kartochka — `NAV_ITEMS` da yo'q: ularga talabnomalar
 * ro'yxatidan yoki "mening navbatim" dan kiriladi.
 *
 * Yaratish yo'li `$requisitionId` dan OLDIN e'lon qilinadi: aks holda `new`
 * so'zi parametrga tushib, mavjud bo'lmagan talabnomaning kartochkasi ochilardi.
 */
const requisitionCreateRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/requisitions/new',
  component: RequisitionCreateRoute,
});

const requisitionCardRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/requisitions/$requisitionId',
  component: RequisitionCardRoute,
});

export const routeTree = rootRoute.addChildren([
  loginRoute,
  shellRoute.addChildren([
    dashboardRoute,
    warehouseListRoute,
    rolesRoute,
    legacyGlobalStockRoute,
    receiptRoute,
    warehouseStockRoute,
    warehouseBuilderRoute,
    cellCardRoute,
    warehouseDetailRoute,
    supplyDetailRoute,
    legacyReceiptRoute,
    legacyStockRoute,
    requisitionCreateRoute,
    requisitionCardRoute,
    myWarehouseRoute,
    writeOffsRoute,
    ...sectionRoutes,
  ]),
]);
